import React from "react";
import { registerOverlay } from "./overlays";
import { useLang } from "./i18n";

// `side` anchors the rotation to the edge the word is pinned to. Rotating about
// the centre swings the far end past the viewport, and body{overflow-x:hidden}
// then shears off the last glyph. The 0.12em padding leaves room for Caveat's
// swash tails, which otherwise get clipped by the clip-path write-on.
//
// Below `md` there's no side margin to sit in, so these used to just vanish
// (`hidden md:block`). They're visible at every size now: `className` is
// expected to carry a FULL responsive set — position, rotation and size as
// plain + `md:`-prefixed Tailwind utilities (e.g. `rotate-[-6deg]
// md:rotate-[-9deg]`) — never as an inline `transform`. Inline styles beat
// every class regardless of breakpoint, so a rotation set that way can't be
// overridden at `md` at all; that's a real bug this file had briefly, caught
// before it shipped. writeMarg (overlays.js) only ever touches opacity and
// clip-path, never transform, so this stays free to vary by breakpoint.
function Word({ id, side = "left", className, children }) {
  return (
    <span
      ref={(el) => registerOverlay(id, el)}
      className={`ff-hand fixed z-[15] block max-w-[62vw] md:max-w-[42vw] ${className}`}
      style={{
        opacity: 0,
        visibility: "hidden",
        clipPath: "inset(0 100% 0 0)",
        color: "var(--bb-red)",
        transformOrigin: side === "right" ? "100% 50%" : "0% 50%",
        padding: "0.08em 0.12em",
      }}
    >
      {children}
    </span>
  );
}

// Handwritten expressions that 'write on' as you scroll. Six pairs, one per
// act (plus the lone "breathe" on the calm act) — on mobile each pair sits as
// a top word / bottom word instead of the desktop's left-margin / right-margin
// pair, since there's no side gutter to use below `md`.
export default function Marginalia() {
  const { t } = useLang();
  const h = t.hand;
  return (
    <div aria-hidden="true">
      <Word id="m-hero-l" side="left" className="top-20 left-[7vw] text-3xl rotate-[-6deg] md:top-[32%] md:left-[3vw] md:text-6xl md:rotate-[-9deg]">{h.heroL}</Word>
      <Word id="m-hero-r" side="right" className="bottom-16 right-[6vw] text-2xl rotate-[4deg] md:top-[58%] md:bottom-auto md:right-[4vw] md:text-6xl md:rotate-[7deg]">{h.heroR}</Word>

      <Word id="m-noise-l" side="left" className="top-20 left-[9vw] text-4xl rotate-[4deg] md:top-[26%] md:left-[4vw] md:text-8xl md:rotate-[-11deg]">{h.noiseL}</Word>
      <Word id="m-noise-r" side="right" className="bottom-16 right-[7vw] text-3xl rotate-[-6deg] md:top-[64%] md:bottom-auto md:right-[5vw] md:text-6xl md:rotate-[8deg]">{h.noiseR}</Word>

      <Word id="m-prod-l" side="left" className="top-20 left-[6vw] text-3xl rotate-[-4deg] md:top-[36%] md:left-[3vw] md:text-6xl md:rotate-[-7deg]">{h.prodL}</Word>
      <Word id="m-prod-r" side="right" className="bottom-16 right-[9vw] text-3xl rotate-[6deg] md:top-[58%] md:bottom-auto md:right-[4vw] md:text-5xl md:rotate-[6deg]">{h.prodR}</Word>

      {/* Promise act. Desktop sits higher/lower than the product pair to stay
          clear of the tall headline stack; same idea holds on mobile. */}
      <Word id="m-promise-l" side="left" className="top-20 left-[7vw] text-3xl rotate-[6deg] md:top-[24%] md:left-[3vw] md:text-6xl md:rotate-[-10deg]">{h.promiseL}</Word>
      <Word id="m-promise-r" side="right" className="bottom-16 right-[6vw] text-2xl rotate-[-4deg] md:top-[68%] md:bottom-auto md:right-[4vw] md:text-5xl md:rotate-[7deg]">{h.promiseR}</Word>

      <Word id="m-price-l" side="left" className="top-20 left-[5vw] text-2xl rotate-[-6deg] md:top-[30%] md:left-[4vw] md:text-5xl md:rotate-[-8deg]">{h.priceL}</Word>
      <Word id="m-price-r" side="right" className="bottom-16 right-[8vw] text-3xl rotate-[4deg] md:top-[64%] md:bottom-auto md:right-[4vw] md:text-5xl md:rotate-[9deg]">{h.priceR}</Word>

      <Word id="m-foot" side="right" className="bottom-16 right-[7vw] text-3xl rotate-[6deg] md:top-[28%] md:bottom-auto md:right-[5vw] md:text-7xl md:rotate-[9deg]">{h.foot}</Word>
    </div>
  );
}
